<template>
  <div
    v-if="items"
    tabindex="0"
    class="ws-radio-list"
    :class="{
      'invisible-wrap': invisibleWrap,
      'no-spacing-y': noSpacingY,
      'no-spacing-x': noSpacingX,
      small,
    }"
  >
    <template
      v-for="(item, index) in showSelectedOnly
        ? [items[selectedIndex || 0]]
        : items"
    >
      <div
        v-if="
          !(
            get(item, 'disable') &&
            $scopedSlots.disabled &&
            $scopedSlots.disabled({ item })
          )
        "
        :key="get(item, uidProp, JSON.stringify(item)) + '_item_' + index"
        class="ws-radio-item item-spacing"
        :class="{
          disabled: get(item, 'disable'),
          selected: selectedIndex === index || showSelectedOnly,
          'selected-bolded': selectedBolded,
        }"
      >
        <label class="flex full-width cursor-pointer">
          <ws-radio
            v-if="!invisibleWrap"
            v-model="selectedIndex"
            :val="index"
            class="q-mt-sm ws-hover-fly-in-trigger"
            :class="{ selected: selectedIndex === index || showSelectedOnly }"
            :color="isTheme(Theme.ADMIN) ? 'blue' : 'green'"
            :disable="get(item, 'disable')"
            @input="$emit('ws-radio-selected')"
          ></ws-radio>
          <span class="flex-1" :class="{'q-ml-md': !invisibleWrap}">
            <slot
              :item="item"
              :index="index"
              :is-selected="selectedIndex === index || showSelectedOnly"
            ></slot>
          </span>
        </label>
        <div
          v-if="$scopedSlots.info"
          class="info"
        >
          <slot
            name="info"
            :item="item"
            :index="index"
            :is-selected="selectedIndex === index || showSelectedOnly"
          ></slot>
        </div>
        <transition name="slide-fade-vertical">
          <div
            v-if="show && index === selectedIndex"
            class="item-content"
          >
            <slot
              name="content"
              class="q-mx-lg"
              :item="item"
              :index="index"
            ></slot>
          </div>
        </transition>
      </div>
      <div
        v-else
        :key="get(item, uidProp, JSON.stringify(item)) + '_disabled_' + index"
        class="item-spacing slot-disabled"
      >
        <slot
          name="disabled"
          :item="item"
          :index="index"
        ></slot>
      </div>
    </template>

    <slot name="extra"></slot>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Watch, Prop} from 'vue-property-decorator';
import { Theme } from '@odin/services';

import {isInteger} from 'lodash-es';
import { WsRadio } from '@odin/components-vue2';

@Component({
  components: {
    WsRadio,
  },
  computed: {
    Theme() {
      return Theme;
    },
  },
})
export class WsRadioList extends Vue {
  @Prop() readonly value!: any;
  @Prop({default: () => []}) readonly items!: any[];
  @Prop({default: 'index'}) readonly mode!: string; // 'index' | 'item'
  @Prop({
    type: Boolean,
    default: true,
  })
  readonly show!: boolean;
  @Prop(Boolean) readonly preselect!: boolean;
  @Prop(Boolean) readonly invisibleWrap!: boolean;
  @Prop(Boolean) readonly noSpacingX!: boolean;
  @Prop(Boolean) readonly noSpacingY!: boolean;
  @Prop(Boolean) readonly small!: boolean;
  @Prop(Boolean) readonly withoutDelimiter!: boolean;
  @Prop({default: 'id'}) readonly uidProp!: string;
  @Prop(Boolean) readonly emitValue!: boolean;
  @Prop(Boolean) readonly showSelectedOnly!: boolean;
  @Prop(Boolean) readonly selectedBolded!: boolean;
  selectedIndex: number | null = null;

  created() {
    if (this.preselect) {
      const validOptions = this.items.filter(item => !item?.disable);
      if (validOptions.length > 0) {
        this.selectedIndex = this.items.indexOf(validOptions[0]);
      }
    }
  }

  @Watch('items')
  onItemsChange() {
    this.onValueChange(this.value);
  }

  @Watch('value', {immediate: true})
  onValueChange(newVal: any) {
    if (newVal === null || newVal === undefined || !this.items.length) {
      this.selectedIndex = null;
    } else if (isInteger(newVal)) {
      this.selectedIndex = newVal;
    } else {
      this.selectIndex(newVal);
    }
  }

  selectIndex(newVal: any) {
    let index = this.items.indexOf(newVal);
    if (index === -1) {
      if (typeof newVal === 'object') {
        index = this.items.findIndex(
          item => item[this.uidProp] === newVal[this.uidProp]
        );
      } else if (typeof newVal === 'string') {
        index = this.items.findIndex(item => item[this.uidProp] === newVal);
      }
    }

    if (index > -1) {
      this.selectedIndex = index;
    } else {
      this.selectedIndex = null;
    }
  }

  @Watch('selectedIndex')
  // eslint-disable-next-line sonarjs/cognitive-complexity
  onSelectedIndexChange() {
    const selectedIndex = this.selectedIndex;

    let item: any;
    if (selectedIndex !== null && selectedIndex < this.items.length) {
      if (this.mode === 'index') {
        this.$emit(
          'input',
          this.emitValue ? this.items[selectedIndex] : selectedIndex
        );
      } else {
        item = this.items[selectedIndex];
        this.$emit('input', this.emitValue ? item[this.uidProp] : item);
      }
    } else {
      this.selectedIndex = null;
    }
  }
}

export default WsRadioList;
</script>

<style lang="scss" scoped>
.ws-radio-list:not(.invisible-wrap) {
  > .ws-radio-item {
    background: $radio-list-item-background;
    border: $radio-list-item-border;
    border-radius: $ws-border-radius;
    padding: map-get($space-sm, y);
    margin-bottom: map-get($space-sm, y);
    transition: background 0.15s ease;

    &:not(.disabled):not(.selected):hover {
      background: $ws-ui-hover;
    }
  }

  &.no-spacing-x > .ws-radio-item {
    &:hover {
      background: none;
    }
  }

  &:not(.no-spacing-x):not(.invisible-wrap) > .ws-radio-item {
    padding: map-get($space-md, x);
  }

  &:not(.no-spacing-y) > .item-spacing + .item-spacing {
    margin-top: map-get($space-sm, y);
  }
}

.flex-1 {
  flex: 1 1 0;
}
</style>
